import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Badge } from '@/components/ui/badge';
import { CommandGroup } from '@/components/ui/command';
import { selectAccountSlug } from '@/redux/session/selectors';

import { CommandLinkItem } from '../CommandLinkItem';
import { type EntityResult } from '../types';
import { buildEntityValue, ENTITY_TYPE_LABELS } from '../utils';
import { TabHint } from './TabHint';

type EntitiesGroupProps = {
  results: EntityResult[];
  onSelect: (path: string, label: string) => void;
  highlightedValue?: string;
};

export const EntitiesGroup = ({ results, onSelect, highlightedValue }: EntitiesGroupProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);

  if (results.length === 0) {
    return null;
  }

  // Group results by entity type
  const grouped = results.reduce<Record<string, EntityResult[]>>((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {});

  return (
    <>
      {Object.entries(grouped).map(([type, items]) => (
        <CommandGroup key={type} heading={t(ENTITY_TYPE_LABELS[type] || type)}>
          {items.map((item) => {
            const Icon = item.icon;
            const value = buildEntityValue(item.type, item.id);
            return (
              <CommandLinkItem
                key={`entity-${item.id}`}
                value={value}
                href={slug ? `/${slug}${item.path}` : '#'}
                onSelect={() => onSelect(item.path, item.name)}
                keywords={[item.name, item.subtitle || '']}
              >
                <Icon />
                <span className="flex-1 truncate">{item.name}</span>
                {item.subtitle && (
                  <Badge
                    type="outline"
                    flavour="neutral"
                    className="ml-2 shrink-0 text-xs font-normal"
                  >
                    {item.subtitle}
                  </Badge>
                )}
                <TabHint value={value} highlightedValue={highlightedValue} pageDrillDown={null} />
              </CommandLinkItem>
            );
          })}
        </CommandGroup>
      ))}
    </>
  );
};
